/**
 * @file Splash screen.
 *
 * Shows the Chainsaw Team logo (placeholder SVG) for 2 seconds, then
 * transitions to the menu. The logo is a simple text placeholder until
 * Jorge provides the actual SVG.
 */

import { GameState } from './GameState.js';
import { GameStates } from './StateMachine.js';

export class SplashState extends GameState {
  /** @type {number} Splash duration in ms. */
  static DURATION = 2000;

  /** @type {number} Fade-in start (ms into splash). */
  static FADE_IN_START = 300;

  /** @type {number} Fade-out start (ms before end). */
  static FADE_OUT_START = 400;

  /**
   * @param {import('../Context.js').GameContext} ctx Shared game context.
   * @param {import('./StateMachine.js').StateMachine} machine Owning machine.
   */
  constructor(ctx, machine) {
    super(ctx, machine);
    /** @type {number | null} @private */
    this._startTime = null;
    /** @type {number | null} @private */
    this._rafId = null;
  }

  /** @returns {void} */
  enter() {
    this._startTime = performance.now();
    this._tick();
  }

  /** @returns {void} */
  exit() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /**
   * Animation loop — updates the splash overlay opacity and transitions
   * to menu when the duration expires.
   * @returns {void}
   * @private
   */
  _tick() {
    const now = performance.now();
    const elapsed = now - this._startTime;
    const total = SplashState.DURATION;
    const fadeOutStart = total - SplashState.FADE_OUT_START;

    const overlay = document.getElementById('splash-overlay');
    if (!overlay) {
      this.machine.change(GameStates.MENU);
      return;
    }

    if (elapsed < SplashState.FADE_IN_START) {
      // Before fade-in: fully transparent
      overlay.style.opacity = '0';
    } else if (elapsed < fadeOutStart) {
      // Fully visible
      overlay.style.opacity = '1';
    } else {
      // Fade out
      const fadeProgress = (elapsed - fadeOutStart) / SplashState.FADE_OUT_START;
      overlay.style.opacity = String(1 - fadeProgress);
    }

    if (elapsed >= total) {
      this.machine.change(GameStates.MENU);
      return;
    }

    this._rafId = requestAnimationFrame(() => this._tick());
  }
}
